import { ReactComponent as ArrowIcon } from "../../assets/icons/chevron_down_black.svg";
import { ReactComponent as DisabledArrowIcon } from "../../assets/icons/chevron_down_grey.svg";
import dropdownStyles from "../../styles/UI/accordion.module.css";
import { useEffect, useRef, useState } from "react";
import inputStyles from "../../styles/UI/inputFields.module.css";

export type AccordionVariant = "PRIMARY" | "SECONDARY";

const Accordion = <T,>({
  onChange,
  variant,
  options,
  labelKey,
  defaultText,
  displayOption,
  label,
  classes,
  leadingElement,
  disabled,
  helperText,
}: {
  onChange: (option: T) => void;
  variant: AccordionVariant;
  options: T[];
  labelKey?: keyof T;
  defaultText: string;
  displayOption?: number; // if user wants to show a partiluar option by index by default
  label?: string;
  classes?: string;
  leadingElement?: (option: T, isSelected: boolean) => React.ReactNode;
  disabled?: boolean;
  helperText?: string;
}) => {
  const [open, toggleOpen] = useState<boolean>();
  const [selectedOption, setSelectedOption] = useState<T>();
  const [displayText, setDisplayText] = useState<string>();
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedOption !== undefined && selectedOption !== null) {
      setDisplayText(
        labelKey
          ? (selectedOption[labelKey] as string)
          : (selectedOption as string),
      );
    } else {
      setDisplayText(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  useEffect(() => {
    if (typeof displayOption === "number") {
      const option = options[displayOption];
      if (option !== undefined) {
        setSelectedOption(option);
        onChange(option);
      }
    }

    const handleClickOutside = (event: PointerEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        toggleOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    return () =>
      document.removeEventListener("pointerdown", handleClickOutside);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`relative flex flex-col gap-[var(--spacing-xs)] ${classes ?? ""}`}
      ref={dropdownRef}
    >
      {variant === "PRIMARY" && (
        <span className={`pl-2 ${inputStyles.label}`}>{label}</span>
      )}
      <button
        disabled={disabled}
        className={`
          ${dropdownStyles.accordion} 
          ${variant === "PRIMARY" ? dropdownStyles.accordion__primary : ""} 
          ${disabled ? dropdownStyles.accordion__disabled : ""}
        `}
        onClick={() => {
          toggleOpen((prevState) => !prevState);
        }}
      >
        <div className="flex gap-[var(--spacing-sm)] justify-start">
          {selectedOption !== undefined &&
            selectedOption !== null &&
            leadingElement &&
            leadingElement(selectedOption, true)}
          <p
            className={`text-left ${displayText !== undefined ? "" : "text-[var(--col-text-secondary)]"}`}
          >
            {displayText !== undefined ? displayText : defaultText}
          </p>
        </div>
        {disabled ? <DisabledArrowIcon /> : <ArrowIcon />}
      </button>
      {helperText && (
        <span className="text-[var(--col-text-secondary)]">{helperText}</span>
      )}
      {open && (
        <ul
          className={`${dropdownStyles.accordionList} ${dropdownStyles.accordionList} `}
        >
          {options.map((option, index) => {
            const isSelected = selectedOption === option;
            return (
              <li
                key={`option_${index}`}
                className={`${isSelected && dropdownStyles.accordionListItem__selected}`}
                onClick={() => {
                  setSelectedOption(option);
                  onChange(option);
                  toggleOpen((prevState) => !prevState);
                }}
              >
                <div className="flex gap-[var(--spacing-sm)]">
                  {leadingElement && leadingElement(option, isSelected)}
                  {labelKey ? (option[labelKey] as string) : (option as string)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Accordion;
